import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import {
  X, MapPin, Phone, Globe, Copy, ExternalLink, Star, Calendar, CheckCircle, Clock, UserPlus
} from "lucide-react";
import { GoogleOutlined } from "@ant-design/icons";
import { GBP } from "../../../types/Listing";
import { useReviews } from "../../../../../components/sharedcomponents/Review/hook/useReviews";

interface ListingDetailsModalProps {
  isOpen:       boolean;
  onClose:      () => void;
  gbp:          GBP | null;
  rating?:      number | null;
  reviewCount?: number;
  subjectEmail?: string;
}

const ListingDetailsModal: React.FC<ListingDetailsModalProps> = ({
  isOpen, onClose, gbp, rating, reviewCount, subjectEmail,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  const locationName = gbp?.accountName && gbp?.locationId
    ? gbp.locationId.startsWith("accounts/")
      ? gbp.locationId
      : `${gbp.accountName}/locations/${gbp.locationId}`
    : undefined;

  const { reviewsData, loading, error } = useReviews({
    isOpen,
    locationName,
    accountName:  gbp?.accountName,
    subjectEmail,
  });

  const displayRating = reviewsData?.averageRating ?? rating ?? null;
  const displayTotal  = reviewsData?.totalReviews    ?? reviewCount ?? 0;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) onClose();
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen || !gbp) return null;

  const addressLines = gbp.address?.addressLines?.join(", ") || "Adresse non renseignée";

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1000] flex items-start justify-center px-4 py-8 overflow-y-auto bg-slate-900/50 backdrop-blur-sm">
      <div
        ref={modalRef}
        className="flex flex-col bg-white w-full max-w-[800px] rounded-2xl overflow-hidden
        shadow-[0_24px_64px_rgba(0,0,0,0.18),0_0_0_1px_rgba(0,0,0,0.06)]
        animate-[gbpFadeIn_0.22s_ease]"
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100 flex-shrink-0">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-50 flex-shrink-0">
            <GoogleOutlined style={{ color: "#0F52BA", fontSize: 18 }} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="m-0 text-[15px] font-bold text-slate-800 tracking-tight leading-tight">
              Détails de l'établissement
            </h2>
            <p className="m-0 text-xs text-slate-400 truncate mt-0.5">{gbp.name}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-100 text-slate-400 hover:bg-rose-50 hover:text-rose-500 hover:border-rose-100 transition-all duration-150"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          <div className="mb-5">
            <h3 className="text-xl font-bold text-slate-800 leading-tight mb-2">{gbp.name}</h3>
            <span className="inline-block bg-blue-50 text-[#0F52BA] text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-lg border border-blue-100">
              {gbp.primaryCategory?.displayName || "Entreprise"}
            </span>
          </div>

          <div className="flex items-center justify-between gap-4 bg-slate-50 border border-slate-100 rounded-xl px-5 py-4 mb-5">
            <div>
              <p className="text-[10px] uppercase tracking-[0.1em] text-slate-400 font-bold mb-2">Réputation Google</p>
              {loading ? (
                <span className="text-slate-400 text-sm">Chargement…</span>
              ) : error ? (
                <span className="text-red-500 text-sm">Erreur: {error}</span>
              ) : (
                <div className="flex items-end gap-3">
                  <span className="text-2xl font-black text-slate-800 leading-none">{displayRating?.toFixed(1) ?? "—"}</span>
                  <div className="pb-0.5">
                    <div className="flex items-center gap-0.5 mb-0.5">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={13}
                          fill={i < Math.round(displayRating || 0) ? "#fbbf24" : "transparent"}
                          className={i < Math.round(displayRating || 0) ? "text-amber-400" : "text-slate-200"} />
                      ))}
                    </div>
                    <p className="text-[11px] text-slate-400 font-medium">{displayTotal} avis clients</p>
                  </div>
                </div>
              )}
            </div>
            {gbp.mapsUri && (
              <a href={gbp.mapsUri} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-xl text-xs font-bold hover:bg-orange-600 transition-colors shadow-sm shadow-orange-200 flex-shrink-0">
                <ExternalLink size={14} /> Voir sur Maps
              </a>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <InfoTile icon={<Phone size={16} className="text-green-500" />} label="Téléphone principal" value={gbp.phone} isLink={`tel:${gbp.phone}`} />
            {gbp.additionalPhones?.map((phone, i) => (
              <InfoTile key={i} icon={<Phone size={16} className="text-green-500" />} label={`Téléphone ${i + 2}`} value={phone} isLink={`tel:${phone}`} />
            ))}
            <InfoTile icon={<MapPin size={16} className="text-red-500" />} label="Adresse" value={addressLines} onCopy={() => navigator.clipboard.writeText(addressLines)} />
            <InfoTile icon={<Globe size={16} className="text-blue-500" />} label="Site web" value={gbp.url} isLink={gbp.url} />
            <InfoTile icon={<Clock size={16} className="text-purple-500" />} label="Statut d'ouverture" value={gbp.openStatus || "Non renseigné"} />
            <InfoTile icon={<Calendar size={16} className="text-orange-500" />} label="Date d'ouverture" value={gbp.openingDate ? `${gbp.openingDate.day}/${gbp.openingDate.month}/${gbp.openingDate.year}` : "Non renseigné"} />
            <InfoTile icon={<CheckCircle size={16} className="text-teal-500" />} label="Clics d'appel" value={gbp.callClicks?.toString() || "0"} />
            <InfoTile icon={<UserPlus size={16} className="text-indigo-500" />} label="Place ID" value={gbp.placeId || "Non renseigné"} />
            {gbp.newReviewUri && (
              <InfoTile icon={<ExternalLink size={16} className="text-orange-500" />} label="Ajouter un avis" value={gbp.newReviewUri} isLink={gbp.newReviewUri} />
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const InfoTile = ({ icon, label, value, isLink, onCopy }: any) => (
  <div className="bg-white border border-slate-100 rounded-xl p-3.5 flex flex-col gap-1 hover:border-slate-200 hover:shadow-sm transition-all duration-150">
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400">
        {icon} {label}
      </span>
      {onCopy && value && (
        <button onClick={onCopy} className="text-slate-300 hover:text-[#0F52BA] transition-colors" title="Copier">
          <Copy size={13} />
        </button>
      )}
    </div>
    <div className="mt-0.5">
      {value ? (
        isLink ? (
          <a href={isLink} target="_blank" rel="noreferrer" className="text-sm font-semibold text-[#0F52BA] hover:underline break-all">
            {value}
          </a>
        ) : (
          <span className="text-sm font-semibold text-slate-700">{value}</span>
        )
      ) : (
        <span className="text-sm font-medium text-slate-300 italic">Non renseigné</span>
      )}
    </div>
  </div>
);

export default ListingDetailsModal;